import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Form, 
  Input, 
  Select, 
  Pagination, 
  Message, 
  Header, 
  Icon, 
  Label, 
  Modal, 
  TextArea,
  Statistic,
  Grid,
  Segment,
  Progress,
  Divider,
  Tab
} from 'semantic-ui-react';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import { API, showError, showInfo, showSuccess, timestamp2string, isAdmin } from '../../helpers';
import useAppList from '../../helpers/useAppList';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const statusOptions = [
  { key: 'all', text: '全部状态', value: '' },
  { key: 'draft', text: '草稿', value: 1 },
  { key: 'published', text: '已发布', value: 2 },
  { key: 'archived', text: '已归档', value: 3 }
];

const typeOptions = [
  { key: 'all', text: '全部类型', value: '' },
  { key: 'automation', text: '自动化脚本', value: 1 },
  { key: 'ai', text: 'AI相关脚本', value: 2 },
  { key: 'dataproc', text: '数据处理', value: 3 },
  { key: 'webscrape', text: '网页抓取', value: 4 },
  { key: 'systemops', text: '系统操作', value: 5 },
  { key: 'utility', text: '实用工具', value: 6 }
];

const difficultyOptions = [
  { key: 'all', text: '全部难度', value: '' },
  { key: 'beginner', text: '初级', value: 1 },
  { key: 'intermediate', text: '中级', value: 2 },
  { key: 'advanced', text: '高级', value: 3 },
  { key: 'expert', text: '专家', value: 4 }
];

const languageOptions = [
  { key: 'all', text: '全部语言', value: '' },
  { key: 'python', text: 'Python', value: 'python' },
  { key: 'javascript', text: 'JavaScript', value: 'javascript' },
  { key: 'bash', text: 'Bash', value: 'bash' },
  { key: 'powershell', text: 'PowerShell', value: 'powershell' },
  { key: 'java', text: 'Java', value: 'java' },
  { key: 'csharp', text: 'C#', value: 'csharp' }
];

const ScriptExampleManagement = () => {
  const [examples, setExamples] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [showStatistics, setShowStatistics] = useState(false);
  const [selectedExample, setSelectedExample] = useState(null);
  const [showExampleModal, setShowExampleModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    code: '',
    natural_language: '',
    type: 1,
    difficulty: 1,
    language: 'python',
    tags: '',
    related_apps: '',
    suitable_devices: '',
    prerequisites: '',
    expected_output: '',
    notes: ''
  });
  const [updateForm, setUpdateForm] = useState({
    example_id: 0,
    title: '',
    description: '',
    code: '',
    natural_language: '',
    type: 1,
    difficulty: 1,
    status: 1,
    language: 'python',
    tags: '',
    related_apps: '',
    suitable_devices: '',
    prerequisites: '',
    expected_output: '',
    notes: ''
  });

  // 使用App列表hook
  const { getAppNames } = useAppList();

  const loadExamples = async () => {
    setLoading(true);
    try {
      let url = `/api/admin/examples?page=${activePage}&page_size=${pageSize}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (typeFilter) url += `&type=${typeFilter}`;
      if (difficultyFilter) url += `&difficulty=${difficultyFilter}`;
      if (languageFilter) url += `&language=${languageFilter}`;
      
      const res = await API.get(url);
      const { success, message, data } = res.data;
      if (success) {
        const examples = data.examples || [];
        const total = data.total || 0;
        setExamples(examples);
        setTotal(total);
      } else {
        showError(message || '获取示例列表失败');
        setExamples([]);
        setTotal(0);
      }
    } catch (error) {
      showError('获取示例列表失败: ' + error.message);
      setExamples([]);
      setTotal(0);
    }
    setLoading(false);
  };

  const loadStatistics = async () => {
    try {
      const res = await API.get('/api/admin/examples/statistics');
      const { success, message, data } = res.data;
      if (success) {
        setStatistics(data);
      } else {
        showError(message || '获取示例统计失败');
      }
    } catch (error) {
      showError('获取示例统计失败: ' + error.message);
    }
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    setActivePage(1);
    await loadExamples();
    setSearchLoading(false);
  };

  const handlePageChange = (e, { activePage }) => {
    setActivePage(activePage);
  };

  const showExampleDetails = async (exampleId) => {
    try {
      const res = await API.get(`/api/admin/examples/${exampleId}`);
      const { success, message, data } = res.data;
      if (success) {
        setSelectedExample(data);
        setShowExampleModal(true);
      } else {
        showError(message || '加载脚本案例详情失败');
      }
    } catch (error) {
      showError('加载脚本案例详情失败: ' + error.message);
    }
  };

  const handleCreateExample = () => {
    setCreateForm({
      title: '',
      description: '',
      code: '',
      natural_language: '',
      type: 1,
      difficulty: 1,
      language: 'python',
      tags: '',
      related_apps: '',
      suitable_devices: '',
      prerequisites: '',
      expected_output: '',
      notes: ''
    });
    setShowCreateModal(true);
  };

  const submitCreate = async () => {
    try {
      // 处理数组字段
      const formData = {
        ...createForm,
        tags: createForm.tags ? createForm.tags.split(',').map(t => t.trim()) : [],
        related_apps: createForm.related_apps ? createForm.related_apps.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id)) : [],
        suitable_devices: createForm.suitable_devices ? createForm.suitable_devices.split(',').map(d => d.trim()) : []
      };

      const res = await API.post('/api/examples/create', formData);
      const { success, message } = res.data;
      if (success) {
        showSuccess('脚本案例创建成功');
        setShowCreateModal(false);
        loadExamples();
      } else {
        showError(message);
      }
    } catch (error) {
      showError('创建失败');
    }
  };

  const handleUpdateExample = (example) => {
    setSelectedExample(example);
    
    // 解析JSON字段
    let tags = [];
    let relatedApps = [];
    let suitableDevices = [];
    
    try {
      if (example.tags) tags = JSON.parse(example.tags);
    } catch (e) {}
    
    try {
      if (example.related_apps) relatedApps = JSON.parse(example.related_apps);
    } catch (e) {}
    
    try {
      if (example.suitable_devices) suitableDevices = JSON.parse(example.suitable_devices);
    } catch (e) {}

    setUpdateForm({
      example_id: example.id,
      title: example.title,
      description: example.description,
      code: example.code,
      natural_language: example.natural_language,
      type: example.type,
      difficulty: example.difficulty,
      status: example.status,
      language: example.language,
      tags: tags.join(', '),
      related_apps: relatedApps.join(', '),
      suitable_devices: suitableDevices.join(', '),
      prerequisites: example.prerequisites,
      expected_output: example.expected_output,
      notes: example.notes
    });
    setShowUpdateModal(true);
  };

  const submitUpdate = async () => {
    try {
      // 处理数组字段
      const formData = {
        ...updateForm,
        tags: updateForm.tags ? updateForm.tags.split(',').map(t => t.trim()) : [],
        related_apps: updateForm.related_apps ? updateForm.related_apps.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id)) : [],
        suitable_devices: updateForm.suitable_devices ? updateForm.suitable_devices.split(',').map(d => d.trim()) : []
      };

      const res = await API.put(`/api/admin/examples/${selectedExample.id}`, formData);
      const { success, message } = res.data;
      if (success) {
        showSuccess('脚本案例更新成功');
        setShowUpdateModal(false);
        loadExamples();
      } else {
        showError(message);
      }
    } catch (error) {
      showError('更新失败');
    }
  };

  const handleDeleteExample = async (exampleId) => {
    if (!window.confirm('确定要删除这个脚本案例吗？删除后无法恢复。')) {
      return;
    }

    try {
      const res = await API.delete(`/api/admin/examples/${exampleId}`);
      const { success, message } = res.data;
      if (success) {
        showSuccess('脚本案例删除成功');
        loadExamples();
      } else {
        showError(message);
      }
    } catch (error) {
      showError('删除失败');
    }
  };

  const handlePublishExample = async (exampleId) => {
    try {
      const res = await API.post(`/api/admin/examples/${exampleId}/publish`);
      const { success, message } = res.data;
      if (success) {
        showSuccess('脚本案例发布成功');
        loadExamples();
      } else {
        showError(message);
      }
    } catch (error) {
      showError('发布失败');
    }
  };

  const handleArchiveExample = async (exampleId) => {
    try {
      const res = await API.post(`/api/admin/examples/${exampleId}/archive`);
      const { success, message } = res.data;
      if (success) {
        showSuccess('脚本案例归档成功');
        loadExamples();
      } else {
        showError(message);
      }
    } catch (error) {
      showError('归档失败');
    }
  };

  useEffect(() => {
    loadExamples();
    if (showStatistics) {
      loadStatistics();
    }
  }, [activePage, statusFilter, typeFilter, difficultyFilter, languageFilter, showStatistics]);

  const getTypeColor = (type) => {
    switch (type) {
      case 1: return 'blue';    // 自动化脚本
      case 2: return 'purple';  // AI相关脚本
      case 3: return 'green';   // 数据处理
      case 4: return 'orange';  // 网页抓取
      case 5: return 'red';     // 系统操作
      case 6: return 'teal';    // 实用工具
      default: return 'grey';
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case 1: return '自动化脚本';
      case 2: return 'AI相关脚本';
      case 3: return '数据处理';
      case 4: return '网页抓取';
      case 5: return '系统操作';
      case 6: return '实用工具';
      default: return '未知';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 1: return 'green';   // 初级
      case 2: return 'yellow';  // 中级
      case 3: return 'orange';  // 高级
      case 4: return 'red';     // 专家
      default: return 'grey';
    }
  };

  const getDifficultyText = (difficulty) => {
    switch (difficulty) {
      case 1: return '初级';
      case 2: return '中级';
      case 3: return '高级';
      case 4: return '专家';
      default: return '未知';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1: return 'grey';    // 草稿
      case 2: return 'green';   // 已发布
      case 3: return 'orange';  // 已归档
      default: return 'grey';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1: return '草稿';
      case 2: return '已发布';
      case 3: return '已归档';
      default: return '未知';
    }
  };

  if (loading && examples.length === 0) {
    return (
      <Segment loading style={{ minHeight: '200px' }}>
        <Header as="h2">
          <Icon name="code" />
          <Header.Content>
            脚本案例库管理
            <Header.Subheader>管理和维护脚本案例库</Header.Subheader>
          </Header.Content>
        </Header>
      </Segment>
    );
  }

  return (
    <div>
      <Header as="h2">
        <Icon name="code" />
        <Header.Content>
          脚本案例库管理
          <Header.Subheader>管理和维护脚本案例库</Header.Subheader>
        </Header.Content>
      </Header>

      {/* 统计概览 */}
      {statistics && showStatistics && (
        <Segment>
          <Header as="h3">
            <Icon name="chart bar" />
            脚本案例库统计概览
          </Header>
          
          <Statistic.Group widths='four'>
            <Statistic>
              <Statistic.Value>{statistics.total_examples}</Statistic.Value>
              <Statistic.Label>总案例数</Statistic.Label>
            </Statistic>
            <Statistic color='green'>
              <Statistic.Value>{statistics.published_examples}</Statistic.Value>
              <Statistic.Label>已发布</Statistic.Label>
            </Statistic>
            <Statistic color='grey'>
              <Statistic.Value>{statistics.draft_examples}</Statistic.Value>
              <Statistic.Label>草稿</Statistic.Label>
            </Statistic>
            <Statistic color='orange'>
              <Statistic.Value>{statistics.archived_examples}</Statistic.Value>
              <Statistic.Label>已归档</Statistic.Label>
            </Statistic>
          </Statistic.Group>

          <Grid stackable style={{ marginTop: '20px' }}>
            <Grid.Row>
              <Grid.Column width={8}>
                <Card fluid>
                  <Card.Header>案例类型分布</Card.Header>
                  <Card.Content>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={statistics.examples_by_type}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="count"
                        >
                          {statistics.examples_by_type.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card.Content>
                </Card>
              </Grid.Column>
              
              <Grid.Column width={8}>
                <Card fluid>
                  <Card.Header>难度分布</Card.Header>
                  <Card.Content>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={statistics.examples_by_difficulty}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="difficulty" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#8884d8" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card.Content>
                </Card>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>
      )}

      {/* 筛选和搜索 */}
      <Segment>
        <Form>
          <Form.Group widths='equal'>
            <Form.Field>
              <label>状态</label>
              <Select
                placeholder="选择状态"
                options={statusOptions}
                value={statusFilter}
                onChange={(e, { value }) => setStatusFilter(value)}
              />
            </Form.Field>
            <Form.Field>
              <label>类型</label>
              <Select
                placeholder="选择类型"
                options={typeOptions}
                value={typeFilter}
                onChange={(e, { value }) => setTypeFilter(value)}
              />
            </Form.Field>
            <Form.Field>
              <label>难度</label>
              <Select
                placeholder="选择难度"
                options={difficultyOptions}
                value={difficultyFilter}
                onChange={(e, { value }) => setDifficultyFilter(value)}
              />
            </Form.Field>
            <Form.Field>
              <label>编程语言</label>
              <Select
                placeholder="选择语言"
                options={languageOptions}
                value={languageFilter}
                onChange={(e, { value }) => setLanguageFilter(value)}
              />
            </Form.Field>
          </Form.Group>
          
          <Form.Group>
            <Form.Field>
              <Button.Group>
                <Button 
                  primary 
                  loading={searchLoading}
                  onClick={handleSearch}
                  icon="search"
                  content="搜索"
                />
                <Button 
                  onClick={() => setShowStatistics(!showStatistics)}
                  icon="chart bar"
                  content={showStatistics ? '隐藏统计' : '显示统计'}
                />
                <Button 
                  color="green"
                  onClick={handleCreateExample}
                  icon="plus"
                  content="添加案例"
                />
              </Button.Group>
            </Form.Field>
          </Form.Group>
        </Form>
      </Segment>

      {/* 脚本案例列表 */}
      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>标题/类型</Table.HeaderCell>
            <Table.HeaderCell>语言/难度</Table.HeaderCell>
            <Table.HeaderCell>关联App</Table.HeaderCell>
            <Table.HeaderCell>状态</Table.HeaderCell>
            <Table.HeaderCell>统计数据</Table.HeaderCell>
            <Table.HeaderCell>创建时间</Table.HeaderCell>
            <Table.HeaderCell>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {examples.map((example) => (
            <Table.Row key={example.id}>
              <Table.Cell>
                <div>
                  <strong>{example.title}</strong>
                  <br />
                  <small style={{ color: '#666' }}>{example.description?.substring(0, 50)}...</small>
                  <br />
                  <Label color={getTypeColor(example.type)} size="small" style={{ marginTop: '5px' }}>
                    {getTypeText(example.type)}
                  </Label>
                </div>
              </Table.Cell>
              <Table.Cell>
                <div>
                  <Label color="blue" size="mini">
                    <Icon name="code" />
                    {example.language}
                  </Label>
                  <br />
                  <Label color={getDifficultyColor(example.difficulty)} size="mini" style={{ marginTop: '5px' }}>
                    <Icon name="star" />
                    {getDifficultyText(example.difficulty)}
                  </Label>
                </div>
              </Table.Cell>
              <Table.Cell>
                <div>
                  {(() => {
                    try {
                      const relatedApps = JSON.parse(example.related_apps || '[]');
                      if (relatedApps.length > 0) {
                        return getAppNames(relatedApps).map((appName, index) => (
                          <Label key={index} size="mini" color="blue" style={{ marginRight: '3px', marginBottom: '2px' }}>
                            <Icon name="mobile alternate" />
                            {appName}
                          </Label>
                        ));
                      } else {
                        return <span style={{ color: '#999', fontSize: '11px' }}>无关联App</span>;
                      }
                    } catch (e) {
                      return <span style={{ color: '#999', fontSize: '11px' }}>无关联App</span>;
                    }
                  })()}
                </div>
              </Table.Cell>
              <Table.Cell>
                <Label color={getStatusColor(example.status)} size="small">
                  {getStatusText(example.status)}
                </Label>
              </Table.Cell>
              <Table.Cell>
                <div style={{ fontSize: '11px' }}>
                  <div><Icon name="eye" />{example.view_count} 浏览</div>
                  <div><Icon name="heart" />{example.like_count} 点赞</div>
                  <div><Icon name="download" />{example.download_count} 下载</div>
                </div>
              </Table.Cell>
              <Table.Cell>
                {timestamp2string(example.created_at)}
              </Table.Cell>
              <Table.Cell>
                <Button.Group size="mini">
                  <Button 
                    icon="eye" 
                    onClick={() => showExampleDetails(example.id)}
                  />
                  <Button 
                    color="blue"
                    icon="edit"
                    onClick={() => handleUpdateExample(example)}
                  />
                  {example.status === 1 && (
                    <Button 
                      color="green"
                      icon="checkmark"
                      onClick={() => handlePublishExample(example.id)}
                    />
                  )}
                  {example.status === 2 && (
                    <Button 
                      color="orange"
                      icon="archive"
                      onClick={() => handleArchiveExample(example.id)}
                    />
                  )}
                  <Button 
                    color="red"
                    icon="trash"
                    onClick={() => handleDeleteExample(example.id)}
                  />
                </Button.Group>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {/* 分页 */}
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <Pagination
          activePage={activePage}
          onPageChange={handlePageChange}
          totalPages={Math.ceil(total / pageSize)}
          ellipsisItem={{ content: <Icon name='ellipsis horizontal' />, icon: true }}
          firstItem={{ content: <Icon name='angle double left' />, icon: true }}
          lastItem={{ content: <Icon name='angle double right' />, icon: true }}
          prevItem={{ content: <Icon name='angle left' />, icon: true }}
          nextItem={{ content: <Icon name='angle right' />, icon: true }}
        />
        <div style={{ marginTop: '10px', color: '#666' }}>
          共 {total} 条记录，第 {activePage} 页
        </div>
      </div>

      {/* 脚本案例详情模态框 */}
      <Modal open={showExampleModal} onClose={() => setShowExampleModal(false)} size="large">
        <Modal.Header>
          <Icon name="code" />
          脚本案例详情 - {selectedExample?.title}
        </Modal.Header>
        <Modal.Content>
          {selectedExample && (
            <Tab panes={[
              {
                menuItem: '基本信息',
                render: () => (
                  <Tab.Pane>
                    <Grid stackable>
                      <Grid.Row>
                        <Grid.Column width={8}>
                          <Segment>
                            <Header as="h4">基本信息</Header>
                            <p><strong>标题:</strong> {selectedExample.title}</p>
                            <p><strong>描述:</strong> {selectedExample.description}</p>
                            <p><strong>类型:</strong> 
                              <Label color={getTypeColor(selectedExample.type)} style={{ marginLeft: '10px' }}>
                                {getTypeText(selectedExample.type)}
                              </Label>
                            </p>
                            <p><strong>难度:</strong> 
                              <Label color={getDifficultyColor(selectedExample.difficulty)} style={{ marginLeft: '10px' }}>
                                {getDifficultyText(selectedExample.difficulty)}
                              </Label>
                            </p>
                            <p><strong>编程语言:</strong> 
                              <Label color="blue" style={{ marginLeft: '10px' }}>
                                {selectedExample.language}
                              </Label>
                            </p>
                            <p><strong>状态:</strong> 
                              <Label color={getStatusColor(selectedExample.status)} style={{ marginLeft: '10px' }}>
                                {getStatusText(selectedExample.status)}
                              </Label>
                            </p>
                          </Segment>
                        </Grid.Column>
                        <Grid.Column width={8}>
                          <Segment>
                            <Header as="h4">统计信息</Header>
                            <p><strong>浏览次数:</strong> {selectedExample.view_count}</p>
                            <p><strong>点赞次数:</strong> {selectedExample.like_count}</p>
                            <p><strong>下载次数:</strong> {selectedExample.download_count}</p>
                            <p><strong>创建时间:</strong> {timestamp2string(selectedExample.created_at)}</p>
                            <p><strong>更新时间:</strong> {timestamp2string(selectedExample.updated_at)}</p>
                          </Segment>
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Tab.Pane>
                )
              },
              {
                menuItem: '代码内容',
                render: () => (
                  <Tab.Pane>
                    <Header as="h4">代码</Header>
                    <pre style={{ 
                      background: '#f4f4f4', 
                      padding: '15px', 
                      borderRadius: '4px',
                      display: 'block',
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                      fontSize: '13px',
                      lineHeight: '1.4',
                      overflow: 'auto',
                      maxHeight: '400px',
                      border: '1px solid #ddd'
                    }}>
                      {selectedExample.code}
                    </pre>
                    
                    {selectedExample.natural_language && (
                      <>
                        <Divider />
                        <Header as="h4">自然语言描述</Header>
                        <p style={{ 
                          background: '#f9f9f9', 
                          padding: '10px', 
                          borderRadius: '4px',
                          borderLeft: '4px solid #2185d0'
                        }}>
                          {selectedExample.natural_language}
                        </p>
                      </>
                    )}
                  </Tab.Pane>
                )
              },
              {
                menuItem: '附加信息',
                render: () => (
                  <Tab.Pane>
                    <Grid stackable>
                      <Grid.Row>
                        <Grid.Column width={16}>
                          {selectedExample.prerequisites && (
                            <Segment>
                              <Header as="h4">前置条件</Header>
                              <p>{selectedExample.prerequisites}</p>
                            </Segment>
                          )}
                          
                          {selectedExample.expected_output && (
                            <Segment>
                              <Header as="h4">预期输出</Header>
                              <p>{selectedExample.expected_output}</p>
                            </Segment>
                          )}
                          
                          {selectedExample.notes && (
                            <Segment>
                              <Header as="h4">备注说明</Header>
                              <p>{selectedExample.notes}</p>
                            </Segment>
                          )}
                          
                          {selectedExample.tags && (
                            <Segment>
                              <Header as="h4">标签</Header>
                              {(() => {
                                try {
                                  const tags = JSON.parse(selectedExample.tags);
                                  return tags.map((tag, index) => (
                                    <Label key={index} size="small" style={{ marginRight: '5px', marginBottom: '5px' }}>
                                      {tag}
                                    </Label>
                                  ));
                                } catch (e) {
                                  return <p>标签格式错误</p>;
                                }
                              })()}
                            </Segment>
                          )}
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Tab.Pane>
                )
              }
            ]} />
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowExampleModal(false)}>
            <Icon name="close" />
            关闭
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 创建脚本案例模态框 */}
      <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)} size="large">
        <Modal.Header>
          <Icon name="plus" />
          添加脚本案例
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Group widths='equal'>
              <Form.Field required>
                <label>标题</label>
                <Input
                  placeholder="输入案例标题..."
                  value={createForm.title}
                  onChange={(e) => setCreateForm({...createForm, title: e.target.value})}
                />
              </Form.Field>
              <Form.Field>
                <label>编程语言</label>
                <Select
                  options={languageOptions.filter(opt => opt.value !== '')}
                  value={createForm.language}
                  onChange={(e, { value }) => setCreateForm({...createForm, language: value})}
                />
              </Form.Field>
            </Form.Group>
            
            <Form.Field>
              <label>描述</label>
              <TextArea
                placeholder="输入案例描述..."
                value={createForm.description}
                onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                rows={2}
              />
            </Form.Field>

            <Form.Field required>
              <label>代码内容</label>
              <TextArea
                placeholder="输入代码..."
                value={createForm.code}
                onChange={(e) => setCreateForm({...createForm, code: e.target.value})}
                rows={8}
                style={{ fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace' }}
              />
            </Form.Field>

            <Form.Field>
              <label>自然语言描述</label>
              <TextArea
                placeholder="用自然语言描述代码的功能..."
                value={createForm.natural_language}
                onChange={(e) => setCreateForm({...createForm, natural_language: e.target.value})}
                rows={3}
              />
            </Form.Field>

            <Form.Group widths='equal'>
              <Form.Field>
                <label>类型</label>
                <Select
                  options={typeOptions.filter(opt => opt.value !== '')}
                  value={createForm.type}
                  onChange={(e, { value }) => setCreateForm({...createForm, type: value})}
                />
              </Form.Field>
              <Form.Field>
                <label>难度</label>
                <Select
                  options={difficultyOptions.filter(opt => opt.value !== '')}
                  value={createForm.difficulty}
                  onChange={(e, { value }) => setCreateForm({...createForm, difficulty: value})}
                />
              </Form.Field>
            </Form.Group>

            <Form.Group widths='equal'>
              <Form.Field>
                <label>标签 (逗号分隔)</label>
                <Input
                  placeholder="python, 自动化, 截图"
                  value={createForm.tags}
                  onChange={(e) => setCreateForm({...createForm, tags: e.target.value})}
                />
              </Form.Field>
              <Form.Field>
                <label>关联App ID (逗号分隔)</label>
                <Input
                  placeholder="1, 2, 3"
                  value={createForm.related_apps}
                  onChange={(e) => setCreateForm({...createForm, related_apps: e.target.value})}
                />
              </Form.Field>
            </Form.Group>

            <Form.Field>
              <label>适用设备 (逗号分隔)</label>
              <Input
                placeholder="desktop, laptop, server"
                value={createForm.suitable_devices}
                onChange={(e) => setCreateForm({...createForm, suitable_devices: e.target.value})}
              />
            </Form.Field>

            <Form.Field>
              <label>前置条件</label>
              <TextArea
                placeholder="运行此脚本需要的前置条件..."
                value={createForm.prerequisites}
                onChange={(e) => setCreateForm({...createForm, prerequisites: e.target.value})}
                rows={2}
              />
            </Form.Field>

            <Form.Field>
              <label>预期输出</label>
              <TextArea
                placeholder="脚本运行后的预期输出..."
                value={createForm.expected_output}
                onChange={(e) => setCreateForm({...createForm, expected_output: e.target.value})}
                rows={2}
              />
            </Form.Field>

            <Form.Field>
              <label>备注说明</label>
              <TextArea
                placeholder="其他需要注意的事项..."
                value={createForm.notes}
                onChange={(e) => setCreateForm({...createForm, notes: e.target.value})}
                rows={2}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowCreateModal(false)}>
            <Icon name="close" />
            取消
          </Button>
          <Button color="green" onClick={submitCreate}>
            <Icon name="checkmark" />
            创建
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 更新脚本案例模态框 */}
      <Modal open={showUpdateModal} onClose={() => setShowUpdateModal(false)} size="large">
        <Modal.Header>
          <Icon name="edit" />
          更新脚本案例
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>标题</label>
                <Input
                  value={updateForm.title}
                  onChange={(e) => setUpdateForm({...updateForm, title: e.target.value})}
                />
              </Form.Field>
              <Form.Field>
                <label>编程语言</label>
                <Select
                  options={languageOptions.filter(opt => opt.value !== '')}
                  value={updateForm.language}
                  onChange={(e, { value }) => setUpdateForm({...updateForm, language: value})}
                />
              </Form.Field>
            </Form.Group>
            
            <Form.Field>
              <label>描述</label>
              <TextArea
                value={updateForm.description}
                onChange={(e) => setUpdateForm({...updateForm, description: e.target.value})}
                rows={2}
              />
            </Form.Field>

            <Form.Field>
              <label>代码内容</label>
              <TextArea
                value={updateForm.code}
                onChange={(e) => setUpdateForm({...updateForm, code: e.target.value})}
                rows={8}
                style={{ fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace' }}
              />
            </Form.Field>

            <Form.Field>
              <label>自然语言描述</label>
              <TextArea
                value={updateForm.natural_language}
                onChange={(e) => setUpdateForm({...updateForm, natural_language: e.target.value})}
                rows={3}
              />
            </Form.Field>

            <Form.Group widths='equal'>
              <Form.Field>
                <label>类型</label>
                <Select
                  options={typeOptions.filter(opt => opt.value !== '')}
                  value={updateForm.type}
                  onChange={(e, { value }) => setUpdateForm({...updateForm, type: value})}
                />
              </Form.Field>
              <Form.Field>
                <label>难度</label>
                <Select
                  options={difficultyOptions.filter(opt => opt.value !== '')}
                  value={updateForm.difficulty}
                  onChange={(e, { value }) => setUpdateForm({...updateForm, difficulty: value})}
                />
              </Form.Field>
            </Form.Group>

            <Form.Field>
              <label>状态</label>
              <Select
                options={statusOptions.filter(opt => opt.value !== '')}
                value={updateForm.status}
                onChange={(e, { value }) => setUpdateForm({...updateForm, status: value})}
              />
            </Form.Field>

            <Form.Group widths='equal'>
              <Form.Field>
                <label>标签 (逗号分隔)</label>
                <Input
                  value={updateForm.tags}
                  onChange={(e) => setUpdateForm({...updateForm, tags: e.target.value})}
                />
              </Form.Field>
              <Form.Field>
                <label>关联App ID (逗号分隔)</label>
                <Input
                  value={updateForm.related_apps}
                  onChange={(e) => setUpdateForm({...updateForm, related_apps: e.target.value})}
                />
              </Form.Field>
            </Form.Group>

            <Form.Field>
              <label>适用设备 (逗号分隔)</label>
              <Input
                value={updateForm.suitable_devices}
                onChange={(e) => setUpdateForm({...updateForm, suitable_devices: e.target.value})}
              />
            </Form.Field>

            <Form.Field>
              <label>前置条件</label>
              <TextArea
                value={updateForm.prerequisites}
                onChange={(e) => setUpdateForm({...updateForm, prerequisites: e.target.value})}
                rows={2}
              />
            </Form.Field>

            <Form.Field>
              <label>预期输出</label>
              <TextArea
                value={updateForm.expected_output}
                onChange={(e) => setUpdateForm({...updateForm, expected_output: e.target.value})}
                rows={2}
              />
            </Form.Field>

            <Form.Field>
              <label>备注说明</label>
              <TextArea
                value={updateForm.notes}
                onChange={(e) => setUpdateForm({...updateForm, notes: e.target.value})}
                rows={2}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowUpdateModal(false)}>
            <Icon name="close" />
            取消
          </Button>
          <Button color="blue" onClick={submitUpdate}>
            <Icon name="checkmark" />
            更新
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default ScriptExampleManagement;
